/**
 * Os passos de `/primeiros-passos` — a lista e quem pode ver cada um.
 *
 * Fica fora da página porque **dois** lugares precisam dela: a tela e o
 * contador da pílula no rodapé da sidebar. Se as duas listas divergissem, o
 * contador diria "2 de 8" numa tela que mostra 3 linhas.
 *
 * O ícone NÃO está aqui de propósito: este arquivo é `.ts` e o ícone é JSX.
 * A tela mapeia `id → ícone`; a sidebar não precisa de ícone nenhum.
 */

import { getUserPermissions, hasPermission, rotaLiberadaPorFuncionalidade } from './permissions';
import type { PassoId } from '../services/OnboardingStatusService';

export type FasePasso = 'atender' | 'time';

export interface PassoDef {
    id: PassoId;
    titulo: string;
    fase: FasePasso;
    /** Todas são telas do modo Configurações, e o "voltar" do trilho de lá
        guarda a última tela de operação — que é esta. Por isso o passo leva
        para a tela cheia sem perder o caminho de volta. */
    rota: string;
    /** Basta uma das permissões. */
    permissoes: string[];
    /** Rótulo do botão enquanto o passo está pendente. */
    acao: string;
}

export const PASSOS: PassoDef[] = [
    { id: 'produtos', titulo: 'Produtos e serviços',   fase: 'atender', rota: '/products',          permissoes: ['gestao_produtos'],     acao: 'Cadastrar' },
    { id: 'agente',   titulo: 'Agente de IA',          fase: 'atender', rota: '/chat-rules',        permissoes: ['regras_chatbot'],      acao: 'Criar' },
    { id: 'canais',   titulo: 'Canais conectados',     fase: 'atender', rota: '/connections',       permissoes: ['gestao_conexoes'],     acao: 'Conectar' },
    { id: 'horario',  titulo: 'Horário de atendimento', fase: 'atender', rota: '/gerenciar-empresa', permissoes: ['dias_funcionamento'],  acao: 'Definir' },
    { id: 'usuarios', titulo: 'Equipe convidada',      fase: 'time',    rota: '/users',             permissoes: ['gestao_usuarios'],     acao: 'Convidar' },
    { id: 'equipes',  titulo: 'Equipes montadas',      fase: 'time',    rota: '/equipes',           permissoes: ['gestao_equipes'],      acao: 'Montar' },
    { id: 'metas',    titulo: 'Metas do mês',          fase: 'time',    rota: '/metas',             permissoes: ['gestao_metas'],        acao: 'Definir' },
    { id: 'motivos',  titulo: 'Motivos de perda',      fase: 'time',    rota: '/motivos-perda',     permissoes: ['gestao_motivo_perda'], acao: 'Cadastrar' },
];

export const TITULO_FASE: Record<FasePasso, string> = {
    atender: 'Para o agente atender',
    time: 'Para organizar o time',
};

/**
 * Os passos que esta pessoa pode ver.
 *
 * Decisão dele em 26/09: *"somente aparece as linhas das telas que ele tem
 * acesso"*. Passo sem permissão some da tela — não aparece desabilitado nem
 * sem botão —, e o total do contador acompanha: quem enxerga 3 passos vê
 * "1 de 3".
 */
export const passosVisiveis = (): PassoDef[] => {
    const permissoes = getUserPermissions();
    // A funcionalidade vem antes da permissão (Sistema › Funcionalidades): passo que
    // leva a uma tela desligada para a empresa seria um controle sem destino.
    return PASSOS.filter(p => rotaLiberadaPorFuncionalidade(p.rota) && hasPermission(permissoes, p.permissoes));
};
